/**
 * ReportPreviewModal Component (Story 5.10, Task 4)
 *
 * Full-screen modal for page-by-page report preview.
 * Features:
 * - Page navigation (Previous/Next + page indicator)
 * - Zoom controls (50%, 75%, 100%)
 * - Keyboard navigation (Left/Right arrows, Escape, +/-)
 * - Accessible: sr-only title, aria-labels, focus management
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { X, CaretLeft, CaretRight, MagnifyingGlassPlus, MagnifyingGlassMinus } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@provenleap/ui";
import { Button } from "@provenleap/ui";
import { ReportCanvas } from "./ReportCanvas";
import type { WidgetConfig } from "../../lib/templateUtils";
import type { WidgetData } from "../../lib/widgetData";
import { splitWidgetsIntoPages } from "../../lib/gridUtils";
import { getGridDimensions } from "@provenleap/types/report-rendering";
import type { ReportFormat } from "@provenleap/types/report-rendering";

export interface ReportPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  widgets: WidgetConfig[];
  widgetData: Record<string, WidgetData>;
  format?: ReportFormat;
}

const ZOOM_LEVELS = [0.5, 0.75, 1.0] as const;
const ZOOM_LABELS = ["50%", "75%", "100%"] as const;

/** Calculate the pixel height of a page's content based on its widgets */
function calculatePageHeight(widgets: WidgetConfig[], contentHeightPx: number, rowHeightPx: number, gapPx: number): number {
  if (widgets.length === 0) return contentHeightPx;
  const maxRow = Math.max(
    ...widgets.map((w) => w.position.row + (w.position.rowSpan ?? 1))
  );
  const minRow = Math.min(...widgets.map((w) => w.position.row));
  const rowCount = maxRow - minRow;
  return rowCount * rowHeightPx + Math.max(0, rowCount - 1) * gapPx;
}

export function ReportPreviewModal({
  open,
  onOpenChange,
  widgets,
  widgetData,
  format = "document",
}: ReportPreviewModalProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [zoomIndex, setZoomIndex] = useState(2); // default: 100%
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const dims = useMemo(() => getGridDimensions(format), [format]);
  const pages = useMemo(
    () => splitWidgetsIntoPages(widgets, dims.contentHeightPx, dims.rowHeightPx, dims.gapPx),
    [widgets, dims.contentHeightPx, dims.rowHeightPx, dims.gapPx]
  );
  const totalPages = pages.length;
  const scale = ZOOM_LEVELS[zoomIndex]!;

  // Reset page when widgets or open state changes
  useEffect(() => {
    if (open) {
      setCurrentPage(0);
      setZoomIndex(2);
    }
  }, [open]);

  // Focus close button when modal opens
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => closeButtonRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [open]);

  const goNextPage = useCallback(() => {
    setCurrentPage((p) => Math.min(p + 1, totalPages - 1));
  }, [totalPages]);

  const goPrevPage = useCallback(() => {
    setCurrentPage((p) => Math.max(p - 1, 0));
  }, []);

  const zoomIn = useCallback(() => {
    setZoomIndex((i) => Math.min(i + 1, ZOOM_LEVELS.length - 1));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomIndex((i) => Math.max(i - 1, 0));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      // Don't handle when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      switch (e.key) {
        case "ArrowRight":
          e.preventDefault();
          goNextPage();
          break;
        case "ArrowLeft":
          e.preventDefault();
          goPrevPage();
          break;
        case "+":
        case "=":
          e.preventDefault();
          zoomIn();
          break;
        case "-":
          e.preventDefault();
          zoomOut();
          break;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, goNextPage, goPrevPage, zoomIn, zoomOut]);

  const currentPageWidgets = pages[currentPage] ?? [];
  const contentHeight = calculatePageHeight(currentPageWidgets, dims.contentHeightPx, dims.rowHeightPx, dims.gapPx);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-w-none w-screen h-screen m-0 rounded-none flex flex-col overflow-y-auto"
        data-testid="preview-modal"
      >
        <DialogTitle className="sr-only">Report Preview</DialogTitle>
        <DialogDescription className="sr-only">
          Full-screen preview of your report. Use arrow keys to navigate pages, plus and minus to zoom.
        </DialogDescription>

        {/* Modal header */}
        <div className="flex items-center justify-between border-b px-3 sm:px-6 py-3 shrink-0">
          <div className="flex items-center gap-4">
            {/* Page navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] min-w-[44px]"
                onClick={goPrevPage}
                disabled={currentPage === 0}
                aria-label="Previous page"
                data-testid="prev-page"
              >
                <CaretLeft size={16} weight="bold" />
              </Button>
              <span
                className="text-sm font-medium min-w-[80px] text-center"
                aria-live="polite"
                data-testid="page-indicator"
              >
                Page {currentPage + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] min-w-[44px]"
                onClick={goNextPage}
                disabled={currentPage === totalPages - 1}
                aria-label="Next page"
                data-testid="next-page"
              >
                <CaretRight size={16} weight="bold" />
              </Button>
            </div>

            {/* Zoom controls */}
            <div className="flex items-center gap-2 border-l pl-4">
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] min-w-[44px]"
                onClick={zoomOut}
                disabled={zoomIndex === 0}
                aria-label="Zoom out"
                data-testid="zoom-out"
              >
                <MagnifyingGlassMinus size={16} />
              </Button>
              <span
                className="text-sm font-medium min-w-[40px] text-center"
                role="status"
                aria-live="polite"
                aria-label={`Current zoom: ${ZOOM_LABELS[zoomIndex]}`}
                data-testid="zoom-level"
              >
                {ZOOM_LABELS[zoomIndex]}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px] min-w-[44px]"
                onClick={zoomIn}
                disabled={zoomIndex === ZOOM_LEVELS.length - 1}
                aria-label="Zoom in"
                data-testid="zoom-in"
              >
                <MagnifyingGlassPlus size={16} />
              </Button>
            </div>
          </div>

          <Button
            ref={closeButtonRef}
            variant="ghost"
            size="sm"
            className="min-h-[44px] min-w-[44px]"
            onClick={() => onOpenChange(false)}
            aria-label="Close preview"
            data-testid="close-preview"
          >
            <X size={20} />
          </Button>
        </div>

        {/* Page content */}
        <div className="flex-1 flex items-start justify-center p-4 sm:p-8 overflow-auto">
          {/* Outer wrapper: clips overflow, compensates height */}
          <div
            className="motion-reduce:transition-none"
            style={{
              overflow: "hidden",
              height: contentHeight * scale,
              width: dims.contentWidthPx * scale,
            }}
            data-testid="zoom-container"
          >
            {/* Inner: natural size, scaled via transform */}
            <div
              style={{
                width: dims.contentWidthPx,
                transform: `scale(${scale})`,
                transformOrigin: "left top",
                willChange: "transform",
              }}
            >
              <ReportCanvas
                widgets={currentPageWidgets}
                widgetData={widgetData}
                editable={false}
                format={format}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
